import { lazy, Suspense, useEffect } from 'react';
import type { ComponentType, ReactNode } from 'react';
import {
  CodeOutlined,
  HomeOutlined,
  SmileOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { Button, Empty, Spin, Typography } from 'antd';
import { Link, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell, toolItems } from './components/AppShell';
import { clearAssetRecoveryMarker, recoverFromAssetLoadError } from './utils/assetRecovery';

function lazyNamed(loader: () => Promise<Record<string, unknown>>, exportName: string) {
  return lazy(async () => {
    try {
      const module = await loader();
      const component = module[exportName] as ComponentType<any> | undefined;
      if (!component) throw new Error(`Page module does not export ${exportName}.`);
      clearAssetRecoveryMarker();
      return { default: component };
    } catch (error) {
      if (recoverFromAssetLoadError(error)) return await new Promise<never>(() => undefined);
      throw error;
    }
  });
}

const LoginPage = lazyNamed(() => import('./pages/LoginPage'), 'default');
const RegisterPage = lazyNamed(() => import('./pages/RegisterPage'), 'default');
const TicketPage = lazyNamed(() => import('./pages/TicketPage'), 'TicketPage');
const ForgotPasswordPage = lazyNamed(() => import('./pages/ForgotPasswordPage'), 'ForgotPasswordPage');
const AuthPolicyPage = lazyNamed(() => import('./pages/AuthPolicyPage'), 'AuthPolicyPage');
const HomePage = lazyNamed(() => import('./pages/HomePage'), 'HomePage');
const RatingRanklistPage = lazyNamed(() => import('./pages/RatingRanklistPage'), 'RatingRanklistPage');
const LandingPage = lazyNamed(() => import('./pages/LandingPage'), 'LandingPage');
const TrainingPlanPage = lazyNamed(() => import('./pages/TrainingPlanPage'), 'TrainingPlanPage');
const SearchPage = lazyNamed(() => import('./pages/SearchPage'), 'SearchPage');
const UserProfilePage = lazyNamed(() => import('./pages/UserProfilePage'), 'UserProfilePage');
const ProblemsPage = lazyNamed(() => import('./pages/ProblemsPage'), 'ProblemsPage');
const ProblemDetailPage = lazyNamed(() => import('./pages/ProblemDetailPage'), 'ProblemDetailPage');
const ProblemSubmitPage = lazyNamed(() => import('./pages/ProblemSubmitPage'), 'ProblemSubmitPage');
const ProblemEditPage = lazyNamed(() => import('./pages/ProblemEditPage'), 'ProblemEditPage');
const ProblemManagePage = lazyNamed(() => import('./pages/ProblemResourcePages'), 'ProblemManagePage');
const ProblemTestdataPage = lazyNamed(() => import('./pages/ProblemResourcePages'), 'ProblemTestdataPage');
const ProblemTestdataSharePage = lazyNamed(() => import('./pages/ProblemResourcePages'), 'ProblemTestdataSharePage');
const ProblemStatisticsPage = lazyNamed(() => import('./pages/ProblemResourcePages'), 'ProblemStatisticsPage');
const ProblemAdditionalFilePage = lazyNamed(() => import('./pages/ProblemResourcePages'), 'ProblemAdditionalFilePage');
const ProblemPrintPage = lazyNamed(() => import('./pages/ProblemResourcePages'), 'ProblemPrintPage');
const ProblemTagEditPage = lazyNamed(() => import('./pages/ProblemTagEditPage'), 'ProblemTagEditPage');
const ProblemTagMergePage = lazyNamed(() => import('./pages/ProblemTagMergePage'), 'ProblemTagMergePage');
const ProblemImportPage = lazyNamed(() => import('./pages/ProblemImportPage'), 'ProblemImportPage');
const ProblemWorkbenchPage = lazyNamed(() => import('./pages/ProblemWorkbenchPage'), 'ProblemWorkbenchPage');
const ContestsPage = lazyNamed(() => import('./pages/ContestsPage'), 'ContestsPage');
const ContestDetailPage = lazyNamed(() => import('./pages/ContestDetailPage'), 'ContestDetailPage');
const ContestEditPage = lazyNamed(() => import('./pages/ContestEditPage'), 'ContestEditPage');
const ContestOrderbooksPage = lazyNamed(() => import('./pages/ContestOrderbooksPage'), 'ContestOrderbooksPage');
const ContestRanklistPage = lazyNamed(() => import('./pages/ContestRanklistPage'), 'ContestRanklistPage');
const ContestSecretLinkPage = lazyNamed(() => import('./pages/ContestSecretLinkPage'), 'ContestSecretLinkPage');
const GroupsPage = lazyNamed(() => import('./pages/GroupsPage'), 'GroupsPage');
const GroupDetailPage = lazyNamed(() => import('./pages/GroupDetailPage'), 'GroupDetailPage');
const GroupContestsPage = lazyNamed(() => import('./pages/GroupContestsPage'), 'GroupContestsPage');
const GroupManagePage = lazyNamed(() => import('./pages/GroupManagePage'), 'GroupManagePage');
const GroupProblemsPage = lazyNamed(() => import('./pages/GroupProblemsPage'), 'GroupProblemsPage');
const GroupCreatePage = lazyNamed(() => import('./pages/GroupCreatePage'), 'GroupCreatePage');
const TrainingsPage = lazyNamed(() => import('./pages/TrainingsPage'), 'TrainingsPage');
const TrainingDetailPage = lazyNamed(() => import('./pages/TrainingsPage'), 'TrainingDetailPage');
const CoursesPage = lazyNamed(() => import('./pages/CoursesPage'), 'CoursesPage');
const CourseDetailPage = lazyNamed(() => import('./pages/CourseDetailPage'), 'CourseDetailPage');
const CourseEditPage = lazyNamed(() => import('./pages/CourseEditPage'), 'CourseEditPage');
const CourseManagePage = lazyNamed(() => import('./pages/CourseManagePage'), 'CourseManagePage');
const DiscussionsPage = lazyNamed(() => import('./pages/DiscussionsPage'), 'DiscussionsPage');
const ArticleDetailPage = lazyNamed(() => import('./pages/ArticleDetailPage'), 'ArticleDetailPage');
const ArticleEditPage = lazyNamed(() => import('./pages/ArticleEditPage'), 'ArticleEditPage');
const SubmissionsPage = lazyNamed(() => import('./pages/SubmissionsPage'), 'SubmissionsPage');
const SubmissionDetailPage = lazyNamed(() => import('./pages/SubmissionDetailPage'), 'SubmissionDetailPage');
const HackAdminPage = lazyNamed(() => import('./pages/HackAdminPage'), 'HackAdminPage');
const SquarePage = lazyNamed(() => import('./pages/SquarePage'), 'SquarePage');
const MessagesPage = lazyNamed(() => import('./pages/MessagesPage'), 'MessagesPage');
const AccountPaymentPage = lazyNamed(() => import('./pages/AccountPaymentPage'), 'AccountPaymentPage');
const AccountQrPayPage = lazyNamed(() => import('./pages/AccountQrPayPage'), 'AccountQrPayPage');
const AccountBillingPage = lazyNamed(() => import('./pages/AccountBillingPage'), 'AccountBillingPage');
const CoinLedgerPage = lazyNamed(() => import('./pages/CoinLedgerPage'), 'CoinLedgerPage');
const AccountFramePage = lazyNamed(() => import('./pages/AccountFramePage'), 'AccountFramePage');
const ReportPage = lazyNamed(() => import('./pages/ReportPage'), 'ReportPage');
const GroupReportPage = lazyNamed(() => import('./pages/GroupReportPage'), 'GroupReportPage');
const StatementPage = lazyNamed(() => import('./pages/StatementPage'), 'StatementPage');
const ApiDocPage = lazyNamed(() => import('./pages/ApiDocPage'), 'ApiDocPage');
const HydroIntegrationPage = lazyNamed(() => import('./pages/HydroIntegrationPage'), 'HydroIntegrationPage');
const HydroIntegrationGuidePage = lazyNamed(() => import('./pages/HydroIntegrationGuide'), 'HydroIntegrationGuidePage');
const ShopPage = lazyNamed(() => import('./pages/ShopPage'), 'ShopPage');
const ScheduleDetailPage = lazyNamed(() => import('./pages/ScheduleDetailPage'), 'ScheduleDetailPage');
const ScheduleEditPage = lazyNamed(() => import('./pages/ScheduleEditPage'), 'ScheduleEditPage');
const GraphEditorToolPage = lazyNamed(() => import('./pages/ToolNativePages'), 'GraphEditorToolPage');
const RanklistToolPage = lazyNamed(() => import('./pages/ToolNativePages'), 'RanklistToolPage');
const SchedulesToolPage = lazyNamed(() => import('./pages/ToolNativePages'), 'SchedulesToolPage');
const ImageHostingToolPage = lazyNamed(() => import('./pages/ToolNativePages'), 'ImageHostingToolPage');
const AppsToolPage = lazyNamed(() => import('./pages/ToolNativePages'), 'AppsToolPage');
const PeoplesToolPage = lazyNamed(() => import('./pages/ToolNativePages'), 'PeoplesToolPage');
const HelpToolPage = lazyNamed(() => import('./pages/ToolNativePages'), 'HelpToolPage');
const PeopleDetailPage = lazyNamed(() => import('./pages/PeoplePages'), 'PeopleDetailPage');
const PeopleEditPage = lazyNamed(() => import('./pages/PeoplePages'), 'PeopleEditPage');
const AiAssistantPage = lazyNamed(() => import('./pages/AiAssistantPage'), 'AiAssistantPage');
const AiChatPage = lazyNamed(() => import('./pages/AiChatPage'), 'AiChatPage');
const AiSharedConversationPage = lazyNamed(() => import('./pages/AiChatPage'), 'AiSharedConversationPage');
const ReviewPage = lazyNamed(() => import('./pages/ReviewPage'), 'ReviewPage');
const AiUsagePage = lazyNamed(() => import('./pages/AiUsagePage'), 'AiUsagePage');
const FilesPage = lazyNamed(() => import('./pages/FilesPage'), 'FilesPage');
const VideoBucketPage = lazyNamed(() => import('./pages/VideoBucketPage'), 'VideoBucketPage');
const SwitchUserPage = lazyNamed(() => import('./pages/SwitchUserPage'), 'SwitchUserPage');
const DuelsPage = lazyNamed(() => import('./pages/DuelPages'), 'DuelsPage');
const DuelCreatePage = lazyNamed(() => import('./pages/DuelPages'), 'DuelCreatePage');
const DuelEditPage = lazyNamed(() => import('./pages/DuelPages'), 'DuelEditPage');
const DuelDetailPage = lazyNamed(() => import('./pages/DuelPages'), 'DuelDetailPage');
const PreliminaryExamListPage = lazyNamed(() => import('./pages/PreliminaryExamPages'), 'PreliminaryExamListPage');
const PreliminaryExamManagePage = lazyNamed(() => import('./pages/PreliminaryExamPages'), 'PreliminaryExamManagePage');
const PreliminaryExamEditPage = lazyNamed(() => import('./pages/PreliminaryExamPages'), 'PreliminaryExamEditPage');
const PreliminaryExamDetailPage = lazyNamed(() => import('./pages/PreliminaryExamPages'), 'PreliminaryExamDetailPage');
const PreliminaryExamRanklistPage = lazyNamed(() => import('./pages/PreliminaryExamPages'), 'PreliminaryExamRanklistPage');

function PlaceholderPage({ title, description, icon }: { title: string; description: string; icon?: ReactNode }) {
  return (
    <div className="front-v2-page">
      <div className="front-v2-page-head">
        <div className="placeholder-title-icon">{icon}</div>
        <div>
          <Typography.Title level={2}>{title}</Typography.Title>
          <Typography.Paragraph type="secondary">{description}</Typography.Paragraph>
        </div>
      </div>
      <div className="front-v2-placeholder">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="新版前台页面占位已就绪，后续会在这里接入真实数据。" />
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="front-v2-page">
      <div className="front-v2-placeholder">
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前前台 v2 路由还没有对应页面。">
          <Button type="primary" icon={<HomeOutlined />}>
            <Link to="/home">回到首页</Link>
          </Button>
        </Empty>
      </div>
    </div>
  );
}

function RouteLoadingPage() {
  return (
    <div className="front-v2-page">
      <div className="front-v2-placeholder">
        <Spin size="large" />
      </div>
    </div>
  );
}

export default function App() {
  const location = useLocation();

  useEffect(() => {
    document.querySelectorAll<HTMLLinkElement>('link[rel]').forEach((link) => {
      if (/(^|\s)icon(\s|$)/i.test(link.rel) || /apple-touch-icon/i.test(link.rel)) link.remove();
    });
    const favicon = document.createElement('link');
    favicon.id = 'front-v2-favicon';
    favicon.rel = 'icon';
    favicon.type = 'image/svg+xml';
    favicon.href = '/favicon-becoder-nav-20260715.svg';
    document.head.appendChild(favicon);
  }, [location.pathname]);

  const routes = (
    <Suspense fallback={<RouteLoadingPage />}>
        <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/ticket" element={<TicketPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/forgot-password/reset" element={<ForgotPasswordPage />} />
        <Route path="/terms" element={<AuthPolicyPage type="terms" />} />
        <Route path="/privacy" element={<AuthPolicyPage type="privacy" />} />
        <Route path="/youth" element={<AuthPolicyPage type="youth" />} />
        <Route path="/home" element={<HomePage />} />
        <Route path="/ratings" element={<RatingRanklistPage />} />
        <Route path="/training-plan" element={<TrainingPlanPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/users/:id" element={<UserProfilePage />} />
        <Route path="/user/:id" element={<UserProfilePage />} />
        <Route path="/problems/tags/merge" element={<ProblemTagMergePage />} />
        <Route path="/problems/tag/:id/edit" element={<ProblemTagEditPage />} />
        <Route path="/problems/import/:source" element={<ProblemImportPage />} />
        <Route path="/problem-workbench/*" element={<ProblemWorkbenchPage />} />
        <Route path="/problems/:id/edit" element={<ProblemEditPage />} />
        <Route path="/problems/:id/manage" element={<ProblemManagePage />} />
        <Route path="/problems/:id/testdata" element={<ProblemTestdataPage />} />
        <Route path="/problems/:id/testdata/share/:filename" element={<ProblemTestdataSharePage />} />
        <Route path="/problems/:id/statistics/:type" element={<ProblemStatisticsPage />} />
        <Route path="/problems/:id/additional-file" element={<ProblemAdditionalFilePage />} />
        <Route path="/problems/:id/print" element={<ProblemPrintPage />} />
        <Route path="/problems/:id/submit" element={<ProblemSubmitPage />} />
        <Route path="/problems/:id" element={<ProblemDetailPage />} />
        <Route path="/problems/*" element={<ProblemsPage />} />
        <Route path="/contests/:id/edit" element={<ContestEditPage />} />
        <Route path="/contests/:id/secret-link" element={<ContestSecretLinkPage />} />
        <Route path="/contests/:id/orderbooks" element={<ContestOrderbooksPage />} />
        <Route path="/contests/:id/ranklist" element={<ContestRanklistPage mode="ranklist" />} />
        <Route path="/contests/:id/after-ranklist" element={<ContestRanklistPage mode="after" />} />
        <Route path="/contests/:id" element={<ContestDetailPage />} />
        <Route path="/contests/*" element={<ContestsPage />} />
        <Route path="/preliminary-exams/manage" element={<PreliminaryExamManagePage />} />
        <Route path="/preliminary-exams/new" element={<PreliminaryExamEditPage />} />
        <Route path="/preliminary-exams/:id/edit" element={<PreliminaryExamEditPage />} />
        <Route path="/preliminary-exams/:id/ranklist" element={<PreliminaryExamRanklistPage />} />
        <Route path="/preliminary-exams/:id" element={<PreliminaryExamDetailPage />} />
        <Route path="/preliminary-exams/*" element={<PreliminaryExamListPage />} />
        <Route path="/groups" element={<GroupsPage />} />
        <Route path="/groups/create" element={<GroupCreatePage />} />
        <Route path="/groups/:id/contests" element={<GroupContestsPage />} />
        <Route path="/groups/:id/problems" element={<GroupProblemsPage />} />
        <Route path="/groups/:id/manage" element={<GroupManagePage />} />
        <Route path="/groups/:id" element={<GroupDetailPage />} />
        <Route path="/submissions/:id" element={<SubmissionDetailPage />} />
        <Route path="/submissions/*" element={<SubmissionsPage />} />
        <Route path="/admin/hacks" element={<HackAdminPage />} />
        <Route path="/articles/:id/edit" element={<ArticleEditPage />} />
        <Route path="/articles/:id" element={<ArticleDetailPage />} />
        <Route path="/discussions/*" element={<DiscussionsPage />} />
        <Route path="/messages/*" element={<MessagesPage />} />
        <Route path="/report" element={<ReportPage />} />
        <Route path="/group-report" element={<GroupReportPage />} />
        <Route path="/statement" element={<StatementPage />} />
        <Route path="/image-hosting/*" element={<ImageHostingToolPage />} />
        <Route path="/graph-editor/*" element={<GraphEditorToolPage />} />
        <Route path="/api-doc" element={<ApiDocPage />} />
        <Route path="/hydro-integration" element={<HydroIntegrationPage />} />
        <Route path="/hydro-integration/guide" element={<HydroIntegrationGuidePage />} />
        <Route path="/schedules/*" element={<SchedulesToolPage />} />
        <Route path="/schedule/:id/edit" element={<ScheduleEditPage />} />
        <Route path="/schedule/:id" element={<ScheduleDetailPage />} />
        <Route path="/oauth/apps/*" element={<AppsToolPage />} />
        <Route path="/peoples/*" element={<PeoplesToolPage />} />
        <Route path="/people/:id/edit" element={<PeopleEditPage />} />
        <Route path="/people/:id" element={<PeopleDetailPage />} />
        <Route path="/help/*" element={<HelpToolPage />} />
        <Route path="/ranklist/*" element={<RanklistToolPage />} />
        <Route path="/account/profile-edit" element={<AccountFramePage page="edit" />} />
        <Route path="/account/promote" element={<AccountFramePage page="promote" />} />
        <Route path="/account/lock" element={<AccountFramePage page="unlock" />} />
        <Route path="/account/renew" element={<AccountPaymentPage />} />
        <Route path="/account/recharge" element={<AccountPaymentPage />} />
        <Route path="/account/token-recharge" element={<AccountPaymentPage />} />
        <Route path="/account/billing" element={<AccountBillingPage />} />
        <Route path="/account/coin-ledger" element={<CoinLedgerPage />} />
        <Route path="/account/payment/qr" element={<AccountQrPayPage />} />
        <Route path="/files/*" element={<FilesPage />} />
        <Route path="/video-bucket/*" element={<VideoBucketPage />} />
        <Route path="/switch-user" element={<SwitchUserPage />} />
        <Route path="/shop/*" element={<ShopPage />} />
        <Route path="/trainings" element={<TrainingsPage />} />
        <Route path="/trainings/:id" element={<TrainingDetailPage />} />
        <Route path="/trainings/:id/introduction" element={<TrainingDetailPage />} />
        <Route path="/courses/manage" element={<CourseManagePage />} />
        <Route path="/courses/:id/edit" element={<CourseEditPage />} />
        <Route path="/courses/:id/introduction" element={<CourseDetailPage />} />
        <Route path="/courses/:id" element={<CourseDetailPage />} />
        <Route path="/courses/*" element={<CoursesPage />} />
        <Route path="/square/*" element={<SquarePage />} />
        <Route path="/groups/*" element={<PlaceholderPage title="用户组" description="用户组、成员、课程和团队题库功能预留入口。" icon={<TeamOutlined />} />} />
        {toolItems.map((item) => (
          <Route key={item.key} path={`${item.key}/*`} element={<PlaceholderPage title={item.label} description="工具页面预留入口，保持在新版前台应用内独立演进。" icon={item.icon} />} />
        ))}
        <Route path="/ai/assistant" element={<AiChatPage />} />
        <Route path="/ai/chat" element={<AiChatPage />} />
        <Route path="/ai/share/:id/:token" element={<AiSharedConversationPage />} />
        <Route path="/ai/problems/:id" element={<AiAssistantPage />} />
        <Route path="/ai/*" element={<AiChatPage />} />
        <Route path="/ai-code/*" element={<PlaceholderPage title="代码生成" description="AI 代码生成与提交辅助入口。" icon={<CodeOutlined />} />} />
        <Route path="/review/*" element={<ReviewPage />} />
        <Route path="/ai-usage/*" element={<AiUsagePage />} />
        <Route path="/admin/ai-usage/*" element={<AiUsagePage global />} />
        <Route path="/duels/*" element={<DuelsPage />} />
        <Route path="/duel/create/*" element={<DuelCreatePage />} />
        <Route path="/duel/:id/edit" element={<DuelEditPage />} />
        <Route path="/duel/:id" element={<DuelDetailPage />} />
        <Route path="/problem-rand/*" element={<PlaceholderPage title="随机一题" description="随机题目入口将接入新版题库数据。" icon={<SmileOutlined />} />} />
        <Route path="*" element={<NotFoundPage />} />
        </Routes>
    </Suspense>
  );

  if (location.pathname === '/') return routes;
  return <AppShell>{routes}</AppShell>;
}
